import {
  Alert,
  Box,
  FormField,
  Input,
  Multiselect,
  NonCancelableCustomEvent,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-core';
import _ from 'lodash';
import { DateTime } from 'luxon';
import {
  ContentAccessStatus,
  ContentAccessUser,
  ContentAudience,
  ContentWorkflow,
  UserRoles,
  WorkflowEventTypes,
  WorkflowState,
  WorkflowTag,
  ContentType,
  PublishedContentVersion,
  TechDomain,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { createWorkflowEventWithStatePolling } from '../../api/CreateWorkflowEventWithStatePolling';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { withAppContext } from '../../contexts/AppContext';
import AppContextProps from '../../contexts/AppContextProps';
import { localLog } from '../../loggers/Logger';
import ButtonDropdownItem from '../../model/ButtonDropdownItem';
import ButtonDropdownSection from '../../model/ButtonDropdownSection';
import { ContentAttributes, WorkflowActions } from '../../model/Enum';
import UserProfile from '../../model/UserProfile';
import AssignTechWriterForm from '../common/AssignTechWriterForm';
import ContentAudienceForm from '../common/ContentAudienceForm';
import ModalDialogDropdownButton from '../common/ModalDialogDropdownButton';
import UserAccessPermissionForm from '../common/UserAccessPermissionForm';
import UserIdAutosuggest from '../common/UserIdAutosuggest';
import { workflowStateDisplayNames, workflowTagDisplayName } from '../utils/DisplayNames';
import {
  draftWorkflowTags,
  publicationWorkflowTags,
  workflowTagOptionsMap,
} from '../utils/SelectableOptions';
import { DemoteAudienceInfo, DemoteAudienceInfoLineItemWorkflow } from './DemoteAudienceInfo';
import { getContentWorkflowTags } from '../utils/WorkflowTagUtils';
import {
  canTechWriterEdit,
  ContentRequestTagKey,
  isPendingToFinalizeWorkflow,
  isRefinalizeWorkflow,
  maxNumberOfPeerReviewers,
} from '../../api/UtilsApi';
import BrowserHistory from '../utils/BrowserHistory';
import { convertEntities } from '@amzn/awscat-editor';
import WorkflowActionUpdateMetadata from './WorkflowActionUpdateMetadata';
import { isResetDraftEnabled, resetDraft } from './ResetDraft';

interface Props extends AppContextProps {
  workflows: ContentWorkflow[];
  allowedActions?: WorkflowActions[];
  customLayout?: boolean;
  onAPGPublished?: (workflows?: ContentWorkflow[]) => void;
  onAPGUnpublished?: (workflows?: ContentWorkflow[]) => void;
  onWorkflowUpdated?: (updatedAttribute?: string, updatedValue?: any) => Promise<void>;
  onWorkflowDeleted?: (workflows?: ContentWorkflow[]) => void;
  onWorkflowPermissionUpdate?: (workflows?: ContentWorkflow[]) => void;
  onWorkflowReviewersUpdate?: (workflows?: ContentWorkflow[]) => void;
  onWorkflowUnpublished?: (workflows?: ContentWorkflow[]) => void;
  showRestorePreviousVersion?: boolean;
}

interface State {
  isModalInputValid?: boolean;
  inputValue?: any; // Union of { authorId: string; authorName: string } | WorkflowTag[] | string depending on selectedAttribute
  inputVersion?: number;
  selectedAttribute?: ContentAttributes;
  validationError?: string;
  contentReviewers?: ContentAccessUser[];
  contentSharedWithUsers?: ContentAccessUser[];
  contentAudience?: ContentAudience;
  selectedTechWriterId?: string;
  techWriters?: UserProfile[];
  demoteTargetAudience: ContentAudience;
  demoteAudienceWorkflowItems: DemoteAudienceInfoLineItemWorkflow[];
  demoteAudienceReason?: string;
  previousVersions?: PublishedContentVersion[];
  contentVersions?: number[];
  selectedVersions?: number[];
  loadingContentVersions?: boolean;
  selectedTechDomains?: {
    [ContentRequestTagKey.MainTechnology]?: TechDomain;
    [ContentRequestTagKey.TechDomain]?: TechDomain[];
  };
}

class WorkflowActionControls extends React.Component<Props, State> {
  private flashbarMessageBroker: FlashbarMessageBroker;
  private mplServiceClient: IMplServiceClient;

  constructor(props: Props) {
    super(props);
    this.mplServiceClient = MplServiceClientFactory.create();
    this.flashbarMessageBroker = FlashbarMessageBroker.create();

    const demoteTargetAudience = ContentAudience.INTERNAL;
    const demoteAudienceWorkflowItems = this.createDemoteItems(
      props.workflows,
      demoteTargetAudience,
    );

    this.state = {
      demoteAudienceWorkflowItems,
      demoteTargetAudience,
      selectedAttribute: undefined,
    };
  }

  public componentDidMount() {
    if (this.props.showRestorePreviousVersion) {
      this.loadPreviousVersionData();
    }
  }

  public render() {
    const { workflows, allowedActions, customLayout } = this.props;
    const { isModalInputValid, demoteAudienceWorkflowItems, demoteTargetAudience } = this.state;
    const numItems = workflows.length;

    if (!Array.isArray(allowedActions) || allowedActions.length === 0) {
      return null;
    }

    const demoteInfo = () => (
      <DemoteAudienceInfo
        workflowItems={demoteAudienceWorkflowItems}
        targetAudience={demoteTargetAudience}
        onAudienceChanged={this.updateTargetAudience}
        onReasonChanged={this.updateDemoteAudienceReason}
      />
    );
    const demoteAudienceButton: ButtonDropdownItem = {
      id: WorkflowActions.DEMOTE_AUDIENCE,
      disabled: this.isDemoteAudienceOptionDisabled(),
      header: 'Demote audience',
      inputForm: demoteInfo,
      onSubmit: this.demoteAudience,
      onCancel: this.resetState,
      submitButtonText: 'Demote',
      text: `Demote audience`,
      onBeforeOpenModal: this.validateDemoteAudienceState,
      requiresInput: true,
    };
    const retractReviewButton: ButtonDropdownItem = {
      id: WorkflowActions.RETRACT_REVIEW,
      disabled: numItems !== 1 || !this.isRetractReviewEnabled(),
      header: 'Retract Review',
      inputForm: this.retractReviewForm,
      modalSize: 'medium',
      onSubmit: this.retractReview,
      submitButtonText: 'Retract Review',
      text: `Retract Review`,
    };

    const unlockWorkflowButton: ButtonDropdownItem = {
      id: WorkflowActions.UNLOCK_WORKFLOW,
      disabled: numItems < 1 || !workflows.every((wf) => wf.isLocked),
      header: constants.ADMIN_UNLOCK_WORKFLOW_MODAL_HEADER,
      inputForm: this.unlockWorkflowsInputForm,
      modalSize: 'medium',
      onSubmit: this.unlockWorkflows,
      submitButtonText: constants.ADMIN_UNLOCK_WORKFLOW_SUBMIT_BUTTON_TEXT,
      text: constants.ADMIN_UNLOCK_WORKFLOW_BUTTON_TEXT,
    };

    const skipDraftReview: ButtonDropdownItem = {
      id: WorkflowActions.SKIP_DRAFT_REVIEW,
      disabled: workflows.some(
        (wf) => wf.publicationState !== WorkflowState.PUBLISHED || !!wf?.skipDraftReview,
      ),
      header: constants.SKIP_DRAFT_REVIEW_MODAL_HEADER,
      inputForm: () => <p>{constants.SKIP_DRAFT_REVIEW_MODAL_TEXT}</p>,
      modalSize: 'medium',
      onSubmit: this.skipDraftReview,
      submitButtonText: constants.SKIP_DRAFT_REVIEW_BUTTON_TEXT,
      text: constants.SKIP_DRAFT_REVIEW_BUTTON_TEXT,
    };

    const unpublishButton: ButtonDropdownItem = {
      id: WorkflowActions.UNPUBLISH,
      disabled: numItems !== 1 || !workflows.every((wf) => !!wf.publicationWorkflowId),
      header: constants.UNPUBLISH_ACTION_MODAL_HEADER,
      modalSize: 'medium',
      inputForm: this.unpublishWorkflowsInputForm,
      onSubmit: this.unpublishWorkflows,
      submitButtonText: constants.UNPUBLISH_BUTTON_TEXT,
      text: constants.UNPUBLISH_BUTTON_TEXT,
    };

    const resetDraftButton: ButtonDropdownItem = {
      id: WorkflowActions.RESET_DRAFT,
      disabled: numItems < 1 || !this.isResetDraftEnabled(),
      header: 'Reset Draft',
      inputForm: this.renderResetDraftInputForm,
      modalSize: 'medium',
      onSubmit: this.resetDraftAction,
      submitButtonText: 'Reset Draft',
      text: 'Reset Draft',
    };

    const workflowItems: ButtonDropdownItem[] = [
      {
        id: WorkflowActions.EDIT_CONTENT,
        disabled: numItems !== 1 || !this.isEditContentEnabled(),
        header: 'Edit content',
        inputForm: () => this.listItemsInModal(),
        onSubmit: this.openContent,
        submitButtonText: 'Edit',
        text: `Edit content`,
      },
      {
        id: WorkflowActions.EDIT_WORKFLOW,
        disabled: !numItems,
        header: 'Edit attributes',
        inputForm: () => this.editWorkflowAttributesForm(),
        onCancel: this.resetState,
        onSubmit: this.updateWorkflowAttribute,
        submitButtonText: 'Save',
        text: `Edit attributes`,
        requiresInput: true,
      },
      {
        id: WorkflowActions.DELETE_WORKFLOW,
        disabled: !numItems,
        header: 'Delete content',
        inputForm: () => this.listItemsInModal(),
        onBeforeOpenModal: this.validateWorkflowDeleteState,
        onSubmit: this.deleteContent,
        submitButtonText: 'Delete',
        text: `Delete`,
      },
      {
        id: WorkflowActions.DELETE_PUBLICATION_VERSION,
        disabled: numItems !== 1,
        header: 'Delete version',
        inputForm: () => this.listContentVersions(),
        onBeforeOpenModal: async () => {
          this.loadHistoryVersions();
          return true;
        },
        onSubmit: this.deletePublicationVersion,
        submitButtonText: 'Delete',
        text: 'Delete version',
      },
      demoteAudienceButton,
      retractReviewButton,
      unlockWorkflowButton,
      skipDraftReview,
      unpublishButton,
      resetDraftButton,
    ].filter((i) => allowedActions.includes(i.id as WorkflowActions));

    const previousVerisonsOptions = this.state.previousVersions?.map((previousVersion) => {
      return {
        header: 'Publish previous version',
        id: previousVersion.versionId,
        text: DateTime.fromISO(previousVersion.lastUpdated.toString()).toLocaleString(
          DateTime.DATETIME_SHORT,
        ),
        disabled: false,
        onSubmit: this.updateVersion,
        inputForm: () => this.listItemsInModal(),
        submitButtonText: 'Publish previous version',
        onBeforeOpenModal: async () => true,
      };
    });

    const apgItems: ButtonDropdownItem[] = [
      {
        id: WorkflowActions.APG_DISTRIBUTE,
        disabled: !numItems,
        header: 'Publish to APG',
        inputForm: () => this.listItemsInModal(),
        onBeforeOpenModal: this.validateAPGDistributeState,
        onSubmit: this.publishOnAPG,
        submitButtonText: 'Publish',
        text: `Publish to APG`,
      },
      {
        id: WorkflowActions.APG_REVOKE,
        disabled: !numItems,
        header: 'Remove from APG',
        inputForm: () => this.listItemsInModal(),
        onBeforeOpenModal: this.validateAPGRevokeState,
        onSubmit: this.unpublishFromAPG,
        submitButtonText: 'Unpublish',
        text: `Remove from APG`,
      },
    ].filter((i) => allowedActions.includes(i.id as WorkflowActions));

    if (Array.isArray(previousVerisonsOptions) && previousVerisonsOptions.length > 0) {
      apgItems.push({
        header: 'Publish previous version',
        id: WorkflowActions.RESTORE_PUBLISHED_CONTENT_VERSION,
        disabled: !numItems,
        text: `Publish previous version`,
        items: previousVerisonsOptions,
      });
    }

    const permissionItems: ButtonDropdownItem[] = [
      {
        id: WorkflowActions.EDIT_RESOURCE_ACCESS,
        disabled: numItems !== 1,
        header: `${constants.EDIT_CO_AUTHORS}`,
        inputForm: () => this.renderPermissionForm(),
        onBeforeOpenModal: this.listContentSharedWith,
        onCancel: this.resetState,
        onSubmit: this.updateSharedContentAccess,
        submitButtonText: 'Save',
        text: `${constants.EDIT_CO_AUTHORS}`,
      },
      {
        id: WorkflowActions.EDIT_RESOURCE_AUDIENCE,
        disabled: numItems !== 1,
        header: constants.CONTENT_AUDIENCE_MODAL_HEADER,
        inputForm: () => this.renderIntendedAudienceForm(),
        onBeforeOpenModal: this.validateNoPublishState,
        onCancel: this.resetState,
        onSubmit: this.updateContentAudience,
        submitButtonText: constants.SAVE_BUTTON_TEXT,
        text: constants.CONTENT_AUDIENCE_LABEL,
      },
    ].filter((i) => allowedActions.includes(i.id as WorkflowActions));

    const reviewerItems: ButtonDropdownItem[] = [
      {
        id: WorkflowActions.EDIT_PEER_REVIEWERS,
        disabled: numItems !== 1, // grant super power to admin
        header: `${constants.EDIT_PEER_REVIEWERS}`,
        inputForm: () => this.renderPeerForm(),
        onBeforeOpenModal: this.listContentReviewers,
        onCancel: this.resetState,
        onSubmit: this.updateContentReviewers,
        submitButtonText: 'Save',
        text: `${constants.EDIT_PEER_REVIEWERS}`,
      },
      {
        id: WorkflowActions.ADD_PEER_REVIEWERS,
        disabled: numItems !== 1,
        header: `${constants.ADD_PEER_REVIEWERS}`,
        inputForm: () => this.renderAddPeerForm(),
        onBeforeOpenModal: this.listContentReviewers,
        onCancel: this.resetState,
        onSubmit: this.updateContentReviewers,
        submitButtonText: 'Save',
        text: `${constants.ADD_PEER_REVIEWERS}`,
      },
      {
        id: WorkflowActions.ASSIGN_TECH_WRITER,
        disabled: numItems !== 1,
        header: `${constants.ASSIGN_TECH_WRITER}`,
        inputForm: () => this.renderAssignTechWriterForm(),
        onBeforeOpenModal: this.listTechWriters,
        onCancel: this.resetState,
        onSubmit: this.assignTechWriter,
        submitButtonText: 'Save',
        text: `${constants.ASSIGN_TECH_WRITER}`,
      },
      {
        id: WorkflowActions.UNASSIGN_TECH_WRITER,
        disabled: numItems !== 1 || !this.getFirstWorkflow()?.techWriterId,
        header: constants.UNASSIGN_TECH_WRITER,
        inputForm: () => this.listItemsInModal(),
        onCancel: this.resetState,
        onSubmit: this.unassignTechWriter,
        submitButtonText: 'Save',
        text: constants.UNASSIGN_TECH_WRITER,
      },
    ].filter((i) => allowedActions.includes(i.id as WorkflowActions));

    if (customLayout) {
      const wfItems: ButtonDropdownSection[] = [
        new ButtonDropdownSection('Workflows', 'Workflows', workflowItems, false),
        new ButtonDropdownSection('Access', 'Access', permissionItems, false),
      ];
      const publishItems: ButtonDropdownSection[] = [
        new ButtonDropdownSection('', '', apgItems, false),
      ];
      return (
        <Box float="right">
          <ModalDialogDropdownButton
            items={wfItems}
            buttonText={'Workflow'}
            disabled={!numItems}
            inputValid={isModalInputValid}
          />
          <ModalDialogDropdownButton
            items={publishItems}
            buttonText={'Public APG'}
            disabled={!numItems}
            inputValid={isModalInputValid}
          />
        </Box>
      );
    } else {
      const allowedControlItems: ButtonDropdownSection[] = [
        new ButtonDropdownSection('Workflows', 'Workflows', workflowItems, false),
        new ButtonDropdownSection('APG', 'Public APG', apgItems, false),
        new ButtonDropdownSection('Access', 'Access', permissionItems, false),
        new ButtonDropdownSection('Reviewer', 'Reviewer', reviewerItems, false),
      ];
      return (
        <Box float="right">
          <ModalDialogDropdownButton
            items={allowedControlItems}
            buttonText={'Actions'}
            disabled={!numItems}
            inputValid={isModalInputValid}
          />
        </Box>
      );
    }
  }

  private loadPreviousVersionData = async () => {
    const { contentId } = this.getFirstWorkflow() ?? {};
    if (contentId) {
      const res = await this.mplServiceClient.getPublishedContent(contentId, ContentType.Pattern);
      this.setState({
        previousVersions: res.publishedContentVersions,
      });
    }
  };

  private loadHistoryVersions = async () => {
    const { contentId } = this.getFirstWorkflow() ?? {};
    if (contentId) {
      this.setState({ loadingContentVersions: true });
      try {
        const workflowHistory = await this.mplServiceClient.listWorkflowHistory(contentId);
        const contentWorkflow = await this.mplServiceClient.getContentWorkflow(contentId);
        const publicationWorkflowId = contentWorkflow?.workflow?.publicationWorkflowId;
        const validWorkflows = workflowHistory?.workflows.filter(
          (workflow) =>
            workflow.state !== WorkflowState.DELETED &&
            workflow.workflowId !== publicationWorkflowId,
        );
        this.setState({
          contentVersions: validWorkflows.map((workflow) => workflow.workflowId),
          selectedVersions: [],
        });
      } finally {
        this.setState({ loadingContentVersions: false });
      }
    }
  };

  private updateVersion = async (id: string) => {
    const { contentId } = this.getFirstWorkflow() ?? {};
    if (contentId) {
      await this.mplServiceClient.updatePublishedContentVersion(contentId, ContentType.Pattern, id);
    }
  };

  private renderPermissionForm = () => {
    const { contentSharedWithUsers } = this.state;
    const workflow = this.getFirstWorkflow();
    return (
      workflow && (
        <UserAccessPermissionForm
          formId={'ShareContentForm'}
          contentId={workflow.contentId}
          contentName={workflow.draftContentMetadata.contentName}
          ownerId={workflow.ownerId}
          onAccessUpdate={this.updateShareWithUsers}
          accessUsers={contentSharedWithUsers}
        />
      )
    );
  };

  private renderPeerForm = () => {
    const { contentReviewers } = this.state;
    const workflow = this.getFirstWorkflow();
    return (
      workflow && (
        <UserAccessPermissionForm
          formId={'EditPeerReviewerForm'}
          contentId={workflow.contentId}
          contentName={workflow.draftContentMetadata.contentName}
          ownerId={workflow.ownerId}
          onAccessUpdate={this.updateReviewers}
          accessUsers={contentReviewers}
          max={maxNumberOfPeerReviewers}
        />
      )
    );
  };

  private renderAddPeerForm = () => {
    const { contentReviewers } = this.state;
    const workflow = this.getFirstWorkflow();
    return (
      workflow && (
        <UserAccessPermissionForm
          formId={'AddPeerReviewerForm'}
          contentId={workflow.contentId}
          contentName={workflow.draftContentMetadata.contentName}
          ownerId={workflow.ownerId}
          onAccessUpdate={this.updateReviewers}
          accessUsers={contentReviewers}
          max={maxNumberOfPeerReviewers}
          isDeleteDisabled
        />
      )
    );
  };

  private renderIntendedAudienceForm = () => {
    const workflow = this.getFirstWorkflow();
    return (
      workflow && (
        <ContentAudienceForm
          formId={'UpdateIntendedAudienceForm'}
          contentId={workflow.contentId}
          contentAudience={workflow.contentAudience}
          onAudienceUpdate={this.updateContentAudienceState}
        />
      )
    );
  };

  private renderAssignTechWriterForm = () => {
    const workflow = this.getFirstWorkflow();
    const { techWriters } = this.state;
    return (
      workflow && (
        <AssignTechWriterForm
          formId={'AssignTechWriterForm'}
          contentId={workflow.contentId}
          techWriterId={workflow.techWriterId}
          techWriters={techWriters ?? []}
          onTechWriterAssignmentUpdate={this.updateTechWriterAssignment}
        />
      )
    );
  };

  private listContentSharedWith = async (): Promise<boolean> => {
    const workflow = this.getFirstWorkflow();
    if (workflow) {
      const { contentId } = workflow;
      const { accessUsers } = await this.mplServiceClient.listContentSharedWith(contentId);
      this.setState({
        contentSharedWithUsers: accessUsers,
      });
      return true;
    }
    return false;
  };

  private listContentReviewers = async () => {
    const workflow = this.getFirstWorkflow();
    if (workflow) {
      const { contentId } = workflow;
      const { accessUsers } = await this.mplServiceClient.listContentReviewers(contentId);
      this.setState({
        contentReviewers: accessUsers,
      });
      return true;
    }
    return false;
  };

  private listTechWriters = async () => {
    const { userProfiles } = await this.mplServiceClient.getTechWriterProfiles();
    const techWriters: UserProfile[] = userProfiles.map((up) => {
      const profile = new UserProfile();
      profile.userId = up.userId;
      profile.name = up.userName;
      return profile;
    });
    this.setState({
      techWriters,
    });
    return true;
  };

  private assignTechWriter = async () => {
    const workflow = this.getFirstWorkflow();
    const { selectedTechWriterId } = this.state;
    if (!workflow || !selectedTechWriterId) return;

    const { onWorkflowUpdated } = this.props;
    await this.mplServiceClient.assignTechWriter(
      workflow.contentId,
      workflow.publicationWorkflowId,
      selectedTechWriterId,
    );
    if (onWorkflowUpdated) {
      onWorkflowUpdated();
    }
  };

  private unassignTechWriter = async () => {
    const workflow = this.getFirstWorkflow();
    if (!workflow) return;

    const { onWorkflowUpdated } = this.props;
    await this.mplServiceClient.assignTechWriter(
      workflow.contentId,
      workflow.publicationWorkflowId,
      '',
    );
    if (onWorkflowUpdated) {
      onWorkflowUpdated();
    }
  };

  private updateWorkflowAttribute = async () => {
    const { workflows, onWorkflowUpdated } = this.props;
    const { selectedAttribute, inputValue, inputVersion, selectedTechDomains } = this.state;
    if (
      (selectedAttribute !== ContentAttributes.Metadata && !inputValue) ||
      (selectedAttribute === ContentAttributes.Metadata &&
        !selectedTechDomains?.[ContentRequestTagKey.MainTechnology]) ||
      inputVersion === undefined
    ) {
      return;
    }

    switch (selectedAttribute) {
      case ContentAttributes.Author:
        const { authorId } = inputValue;
        if (authorId) {
          await Promise.all(
            workflows.map((i) =>
              this.mplServiceClient.postWorkflowEvent(
                i.contentId,
                inputVersion,
                WorkflowEventTypes.CHANGE_OWNER,
                '',
                { newOwnerId: authorId },
              ),
            ),
          );
        }
        break;
      case ContentAttributes.Tags:
        const draftContentTags = new Set<WorkflowTag>(
          // @ts-expect-error TS7006 - strict migration
          inputValue.filter((value) => draftWorkflowTags.includes(value)),
        );
        const publicationTags = new Set<WorkflowTag>(
          // @ts-expect-error TS7006 - strict migration
          inputValue.filter((value) => publicationWorkflowTags.includes(value)),
        );
        await Promise.all(
          workflows.map(
            (i) =>
              !_.isEqual(i.draftWorkflowTags, draftContentTags) &&
              this.mplServiceClient.updateWorkflowTags(i.contentId, draftContentTags),
          ),
        );
        await Promise.all(
          workflows.map(
            (i) =>
              !_.isEqual(i.lastPublicationWorkflowTags, publicationTags) &&
              i.publicationWorkflows?.length > 0 &&
              this.mplServiceClient.updatePublicationTags(i.contentId, publicationTags),
          ),
        );
        break;
      case ContentAttributes.Rank:
        await Promise.all(
          workflows.map((i) =>
            this.mplServiceClient.updateWorkflowRank(i.contentId, Number(inputValue)),
          ),
        );
        break;
      case ContentAttributes.Metadata:
        await Promise.all(
          workflows.map(
            (wf) =>
              inputVersion &&
              selectedTechDomains?.[ContentRequestTagKey.MainTechnology] &&
              this.mplServiceClient.updateContentTemplateMetadataTechDomains(
                wf.contentId,
                inputVersion,
                selectedTechDomains[ContentRequestTagKey.MainTechnology],
                selectedTechDomains[ContentRequestTagKey.TechDomain],
              ),
          ),
        );
        break;
      default:
        // Do nothing
        return;
    }

    this.resetState();

    if (onWorkflowUpdated) {
      onWorkflowUpdated(selectedAttribute, inputValue);
    }
  };

  private deleteContent = async () => {
    const { workflows, onWorkflowDeleted } = this.props;
    const { entities } = this.props.appContext;
    const deleted: ContentWorkflow[] = [];
    const notDeleted: ContentWorkflow[] = [];

    await Promise.all(
      workflows.map(async (wf) => {
        const contentId = wf.contentId;
        try {
          await this.mplServiceClient.deleteContent(contentId);
          deleted.push(wf);
        } catch (e) {
          localLog.debug(
            `Delete content ${wf.draftContentMetadata.contentName} failed : ${(e as Error).message}`,
          );
          notDeleted.push(wf);
        }
      }),
    );

    if (notDeleted.length > 0) {
      this.flashbarMessageBroker.pushErrorMessage(
        `Unable to delete [${notDeleted
          .map((i) => convertEntities(i.draftContentMetadata.contentName, entities))
          .join(', ')}]`,
      );
    }

    if (deleted.length > 0) {
      this.flashbarMessageBroker.pushSuccessMessage(
        `Contents [${deleted
          .map((i) => convertEntities(i.draftContentMetadata.contentName, entities))
          .join(', ')}] have been successfully deleted`,
      );
      if (onWorkflowDeleted) {
        onWorkflowDeleted(deleted);
      }
    }
  };

  private deletePublicationVersion = async () => {
    const { workflows } = this.props;
    const { selectedVersions } = this.state;
    const deleted: number[] = [];
    const notDeleted: number[] = [];
    const contentId = workflows[0]?.contentId;

    // TODO: send selectedVersion as an array to deletePublicationVersion
    // when we upgrade aws-serverless-express version to support multiple query parameters
    if (Array.isArray(selectedVersions) && contentId) {
      await Promise.all(
        selectedVersions.map(async (selectedVersion) => {
          try {
            await this.mplServiceClient.deletePublicationVersion(contentId, [selectedVersion]);
            deleted.push(selectedVersion);
          } catch (e) {
            localLog.debug(
              `Delete publication version ${selectedVersion} failed: ${(e as Error).message}`,
            );
            notDeleted.push(selectedVersion);
          }
        }),
      );
    }

    if (notDeleted.length > 0) {
      this.flashbarMessageBroker.pushErrorMessage(
        `Unable to delete version(s) [${notDeleted.join(', ')}]`,
      );
    }

    if (deleted.length > 0) {
      this.flashbarMessageBroker.pushSuccessMessage(
        `Version(s) [${deleted.join(', ')}] have been successfully deleted`,
      );
    }
  };

  private updateTargetAudience = (demoteTargetAudience: ContentAudience) => {
    const newItems = this.createDemoteItems(this.props.workflows, demoteTargetAudience);
    this.setState({
      demoteAudienceWorkflowItems: newItems,
      demoteTargetAudience,
    });
  };

  private updateDemoteAudienceReason = (reason: string, isValid: boolean) => {
    this.setState({
      demoteAudienceReason: reason,
      isModalInputValid: isValid,
    });
  };

  private isDemoteAudienceOptionDisabled = () => {
    const { demoteAudienceWorkflowItems } = this.state;
    if (demoteAudienceWorkflowItems.length < 1) {
      return true;
    }
    if (demoteAudienceWorkflowItems.every((i) => i.audience === ContentAudience.INTERNAL)) {
      return true;
    }

    return false;
  };

  private demoteAudience = async () => {
    const { entities } = this.props.appContext;
    await Promise.all(
      this.state.demoteAudienceWorkflowItems.map(async (wfi) => {
        try {
          await createWorkflowEventWithStatePolling(
            wfi.contentId,
            wfi.version,
            WorkflowEventTypes.DEMOTE_AUDIENCE,
            this.state.demoteAudienceReason ?? '',
            { demoteToAudience: this.state.demoteTargetAudience },
          );
        } catch (e) {
          this.flashbarMessageBroker.pushErrorMessage(
            `Could not demote content ${convertEntities(wfi.contentName, entities)}`,
          );
        }
      }),
    );
    await this.props.onWorkflowUpdated?.();
    return true;
  };

  private publishOnAPG = async () => {
    const { workflows, onAPGPublished } = this.props;
    await Promise.all(
      workflows.map((i) => {
        const found = i.publicationWorkflows.find(
          (wf) => wf.state === WorkflowState.FINALIZED || wf.state === WorkflowState.REVOKED,
        );
        if (!found) return Promise.resolve();
        return this.mplServiceClient.postWorkflowEvent(
          found.contentId,
          found.workflowId,
          WorkflowEventTypes.DISTRIBUTE,
          '',
        );
      }),
    );

    if (onAPGPublished) {
      onAPGPublished(workflows);
    }
  };

  private unpublishFromAPG = async () => {
    const { workflows, onAPGUnpublished } = this.props;
    await Promise.all(
      workflows.map((i) => {
        const found = i.publicationWorkflows.find((wf) => wf.state === WorkflowState.DISTRIBUTED);
        if (!found) return Promise.resolve();
        return this.mplServiceClient.postWorkflowEvent(
          found.contentId,
          found.workflowId,
          WorkflowEventTypes.REVOKE,
          '',
        );
      }),
    );

    if (onAPGUnpublished) {
      onAPGUnpublished(workflows);
    }
  };

  private validateAPGDistributeState = async () => {
    const { workflows } = this.props;
    let message;
    if (
      workflows.some((i) => {
        const { contentAudience, publicationWorkflows } = i;
        const [fw] = publicationWorkflows;
        if (fw?.state === WorkflowState.DISTRIBUTED) {
          message = 'A content has already been distributed to APG';
          return true;
        } else if ([ContentAudience.INTERNAL, ContentAudience.EXTERNAL].includes(contentAudience)) {
          message = constants.CONTENT_AUDIENCE_CANNOT_PUBLISH_MESSAGE;
          return true;
        } else {
          message =
            'Cannot distribute to public APG if a content is not in Finalized or Revoked state';
          return !publicationWorkflows.some((wf) =>
            [WorkflowState.FINALIZED, WorkflowState.REVOKED].includes(wf.state),
          );
        }
      })
    ) {
      if (message) this.flashbarMessageBroker.pushWarnMessage(message);
      return false;
    }
    return true;
  };

  private validateAPGRevokeState = async () => {
    const { workflows } = this.props;
    if (
      workflows.some(
        (i) => !i.publicationWorkflows.some((wf) => wf.state === WorkflowState.DISTRIBUTED),
      )
    ) {
      this.flashbarMessageBroker.pushWarnMessage(
        'Cannot revoke if a content has not been distributed on public APG',
      );
      return false;
    }
    return true;
  };

  private validateNoPublishState = async () => {
    const workflow = this.getFirstWorkflow();
    if (!workflow) return false;

    if (!!workflow.publicationState) {
      this.flashbarMessageBroker.pushWarnMessage(constants.CONTENT_AUDIENCE_CANNOT_EDIT_PUBLISHED);
      return false;
    }
    if (![WorkflowState.IN_REVIEW, WorkflowState.REVIEWED].includes(workflow.draftState)) {
      this.flashbarMessageBroker.pushWarnMessage(constants.CONTENT_AUDIENCE_CANNOT_EDIT_DRAFT);
      return false;
    }
    return true;
  };

  private validateWorkflowDeleteState = async () => {
    const { workflows } = this.props;
    if (
      workflows.some(
        (i) =>
          [WorkflowState.REVIEWED].includes(i.draftState) ||
          (!!i.publicationState &&
            [
              WorkflowState.PUBLISHED,
              WorkflowState.FINALIZING,
              WorkflowState.FINALIZED,
              WorkflowState.REVOKED,
              WorkflowState.DISTRIBUTED,
            ].includes(i.publicationState)),
      )
    ) {
      this.flashbarMessageBroker.pushWarnMessage(constants.DELETE_CONTENT_INFO_MESSAGE);
      return false;
    }
    return true;
  };

  private validateDemoteAudienceState = async () => {
    const { workflows } = this.props;
    if (workflows.some((i) => WorkflowState.FINALIZING === i.publicationState)) {
      this.flashbarMessageBroker.pushWarnMessage(
        'One or more selected content are in Techwriter finalizing stage. Please reverse or finalize the pending changes before demoting',
      );
      return false;
    }
    return true;
  };

  private updateChangedWorkflowAttribute = ({
    detail: { selectedOption },
  }: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    let inputValue;
    if (selectedOption.value === ContentAttributes.Tags) {
      const workflow = this.getFirstWorkflow();
      if (workflow) inputValue = [...getContentWorkflowTags(workflow)];
    }

    this.setState(
      {
        inputValue,
        selectedAttribute:
          ContentAttributes[selectedOption.value as keyof typeof ContentAttributes],
        validationError: undefined,
        isModalInputValid: true,
      },
      () => this.validateInput(),
    );
  };

  private resetState = () => {
    this.setState({
      contentReviewers: [],
      contentSharedWithUsers: [],
      inputValue: undefined,
      selectedAttribute: undefined,
      validationError: undefined,
      isModalInputValid: undefined,
      inputVersion: undefined,
      selectedTechDomains: {},
    });
  };

  private updateContentAuthor = (userId: string, userName: string, validationError: any) => {
    this.setState(
      {
        inputValue: {
          authorId: userId,
          authorName: userName,
        },
        validationError,
      },
      () => this.validateInput(),
    );
  };

  private updateContentAudienceState = (contentAudience: ContentAudience) => {
    this.setState({
      contentAudience,
    });
  };

  private updateTechWriterAssignment = (techWriterId: string) => {
    this.setState({
      selectedTechWriterId: techWriterId,
    });
  };

  private updateInputValue = (event: any) => {
    this.setState(
      {
        inputValue: event.detail.value,
      },
      () => this.validateInput(),
    );
  };

  private updateInputVersion = ({ detail }: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
    this.setState(
      {
        inputVersion: Number(detail.selectedOption.value),
      },
      () => this.validateInput(),
    );
  };

  private listItemsInModal = () => {
    const { workflows } = this.props;
    const { entities } = this.props.appContext;
    return (
      <div>
        {workflows.map((i) => (
          <div key={i.contentId} className="contentBox">
            {' '}
            {convertEntities(i.draftContentMetadata.contentName, entities)}{' '}
          </div>
        ))}
      </div>
    );
  };

  private listContentVersions = () => {
    const { contentVersions, selectedVersions, loadingContentVersions } = this.state;
    if (loadingContentVersions) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner />
        </Box>
      );
    }
    // Always render the select to avoid confusion
    // otherwise end user may think it just a confirmation dialog
    const versions = [...(contentVersions ?? [])];
    versions.sort((num1, num2) => num1 - num2);
    const versionItems = versions.map((version) => ({
      id: version.toString(),
      label: `Version: ${version}`,
      value: version.toString(),
    }));

    return (
      <div>
        <Multiselect
          selectedOptions={(selectedVersions ?? []).map((selectedVersion) => ({
            value: selectedVersion.toString(),
            label: `Version: ${selectedVersion}`,
          }))}
          onChange={({ detail }) => {
            this.setState({
              selectedVersions: detail.selectedOptions.map((selectedOption) =>
                Number(selectedOption.value),
              ),
            });
          }}
          options={versionItems}
          placeholder="Select version to delete"
          deselectAriaLabel={() => 'Remove version'}
          ariaRequired
        />
      </div>
    );
  };

  /**
   * Update the selected tech domains in state
   * @param mainTech main techology
   * @param techDomains additional techologies
   */
  private updateSelectedTechDomains = (mainTech: TechDomain, techDomains?: TechDomain[]) => {
    this.setState({
      selectedTechDomains: {
        [ContentRequestTagKey.MainTechnology]: mainTech,
        [ContentRequestTagKey.TechDomain]: techDomains,
      },
    });
  };

  private renderInputField = (attribute: ContentAttributes | undefined) => {
    const { validationError, inputVersion, inputValue, selectedAttribute } = this.state;
    const workflow = this.getFirstWorkflow();

    if (!attribute) {
      return null;
    }
    let inputForm;

    switch (attribute) {
      case ContentAttributes.Author:
        inputForm = <UserIdAutosuggest onUserSelected={this.updateContentAuthor} />;
        break;
      case ContentAttributes.Rank:
        inputForm = (
          <Input
            value={inputValue}
            type="number"
            onChange={this.updateInputValue}
            placeholder={constants.WORKFLOW_RANK_PLACEHODER}
            ariaRequired
          />
        );
        break;
      case ContentAttributes.Tags:
        inputForm = (
          <Multiselect
            // @ts-expect-error TS7006 - strict migration
            selectedOptions={inputValue.map((i) => ({
              value: i,
              label: workflowTagDisplayName.get(i),
            }))}
            onChange={({ detail }) =>
              this.setState({ inputValue: detail.selectedOptions.map((i) => i.value) })
            }
            options={workflowTagOptionsMap}
            placeholder="Choose tags"
            selectedAriaLabel="Selected"
            keepOpen={false}
            deselectAriaLabel={() => 'Remove tag'}
            ariaRequired
          />
        );
        break;
      case ContentAttributes.Metadata:
        inputForm =
          workflow && inputVersion ? (
            <WorkflowActionUpdateMetadata
              workflow={workflow}
              version={inputVersion}
              validateInput={this.validateInput}
              updateSelectedTechDomains={this.updateSelectedTechDomains}
            />
          ) : null;
        break;
      default:
        return;
    }

    const versionSelect =
      selectedAttribute === ContentAttributes.Author ||
      selectedAttribute === ContentAttributes.Metadata ? (
        <FormField
          label={'Version'}
          errorText={validationError}
          stretch
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Select
            selectedOption={
              inputVersion ? { label: `Version ${inputVersion}`, value: `${inputVersion}` } : null
            }
            onChange={this.updateInputVersion}
            options={[
              {
                label: `Version ${workflow?.draftWorkflowId} (Draft)`,
                value: `${workflow?.draftWorkflowId}`,
              },
              ...(workflow?.publicationWorkflows ?? []).map((wf) => ({
                label: `Version ${wf.workflowId} (${workflowStateDisplayNames.get(wf.state)})`,
                value: `${wf.workflowId}`,
              })),
            ]}
            selectedAriaLabel="Selected version"
            ariaRequired
          />
        </FormField>
      ) : null;

    return (
      <SpaceBetween size="m">
        {versionSelect}
        {selectedAttribute === ContentAttributes.Metadata ? (
          inputForm
        ) : (
          <FormField
            label={'Value'}
            errorText={validationError}
            stretch
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            {' '}
            {inputForm}
          </FormField>
        )}
      </SpaceBetween>
    );
  };

  private editWorkflowAttributesForm = () => {
    const { authnInfo } = this.props.appContext;
    const userProfile = authnInfo?.userProfile;
    const { selectedAttribute } = this.state;
    const { workflows } = this.props;

    const attributeTypeUserRoleRestriction = new Map<ContentAttributes, UserRoles[]>([
      [ContentAttributes.Author, [UserRoles.Admin, UserRoles.TechWriter]],
      [ContentAttributes.Tags, [UserRoles.Admin, UserRoles.TechWriter]],
      [ContentAttributes.Rank, [UserRoles.Admin, UserRoles.TechWriter, UserRoles.Publisher]],
      [ContentAttributes.Metadata, [UserRoles.Admin]],
    ]);

    const attributeTypeAllowedMap = new Map<ContentAttributes, boolean>([
      [ContentAttributes.Author, workflows.length === 1],
      [ContentAttributes.Tags, workflows.length === 1],
      [ContentAttributes.Rank, true],
      [ContentAttributes.Metadata, workflows.length === 1],
    ]);

    const allowedAttributesToEdit = (
      Object.keys(ContentAttributes) as Array<keyof typeof ContentAttributes>
    )
      .filter((att) => {
        const allowedRoles = attributeTypeUserRoleRestriction.get(ContentAttributes[att]);
        return Array.isArray(allowedRoles) && allowedRoles.some((r) => userProfile?.roles?.has(r));
      })
      .map((att) => {
        return {
          value: att,
          label: att,
          disabled: !attributeTypeAllowedMap.get(ContentAttributes[att]),
        };
      });

    return (
      <SpaceBetween size="m">
        {selectedAttribute === ContentAttributes.Metadata && !!workflows?.[0]?.lock && (
          <Alert type="warning">Content is locked and cannot be updated</Alert>
        )}
        {this.listItemsInModal()}
        <FormField
          label={constants.EDIT_ATTRIBUTE_LABEL}
          stretch
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Select
            selectedOption={{ label: selectedAttribute, value: selectedAttribute }}
            placeholder={constants.SELECT_CONTENT_ATTRIBUTE_TO_UPDATE_PLACEHOLDER}
            options={allowedAttributesToEdit.map((f) => ({
              value: f.value,
              label: f.label,
              disabled: f.disabled,
            }))}
            onChange={this.updateChangedWorkflowAttribute}
            ariaRequired
          />
        </FormField>
        {this.renderInputField(selectedAttribute)}
      </SpaceBetween>
    );
  };

  private getUserUpdateStats = (
    accessUsers: ContentAccessUser[],
  ): { newUserIds: string[]; removedUserIds: string[] } => {
    const newUserIds = accessUsers
      .filter((us) => us.status === ContentAccessStatus.PENDING_ADD)
      .map((u) => u.userId);
    const removedUserIds = accessUsers
      .filter((us) => us.status === ContentAccessStatus.PENDING_DELETE)
      .map((u) => u.userId);
    return {
      newUserIds,
      removedUserIds,
    };
  };

  private updateContentReviewers = async () => {
    const { workflows } = this.props;
    const { contentReviewers } = this.state;
    const { newUserIds, removedUserIds } = this.getUserUpdateStats(contentReviewers ?? []);
    const contentIds = workflows.map((wf) => wf.contentId);
    if (newUserIds.length) {
      await this.mplServiceClient.addContentReviewers(contentIds, newUserIds);
    }
    if (removedUserIds.length) {
      await this.mplServiceClient.removeContentReviewers(contentIds, removedUserIds);
    }
    const { onWorkflowReviewersUpdate } = this.props;
    if (onWorkflowReviewersUpdate) {
      onWorkflowReviewersUpdate();
    }
  };

  private updateSharedContentAccess = async () => {
    const { workflows } = this.props;
    const { contentSharedWithUsers } = this.state;
    const { newUserIds, removedUserIds } = this.getUserUpdateStats(contentSharedWithUsers ?? []);
    const contentIds = workflows.map((wf) => wf.contentId);
    if (newUserIds.length) {
      await this.mplServiceClient.shareContents(contentIds, newUserIds);
    }
    if (removedUserIds.length) {
      await this.mplServiceClient.unShareContents(contentIds, removedUserIds);
    }
    const { onWorkflowPermissionUpdate } = this.props;
    if (onWorkflowPermissionUpdate) {
      onWorkflowPermissionUpdate();
    }
  };

  private updateContentAudience = async () => {
    const workflow = this.getFirstWorkflow();
    const { contentId } = workflow ?? {};
    const { contentAudience } = this.state;
    if (!contentId || !contentAudience) return;

    const { onWorkflowUpdated } = this.props;
    await this.mplServiceClient.updateWorkflowContentAudience(contentId, contentAudience);

    if (onWorkflowUpdated) {
      onWorkflowUpdated();
    }
  };

  private updateShareWithUsers = (shareWithUsers: ContentAccessUser[]) => {
    this.setState({
      contentSharedWithUsers: shareWithUsers,
      validationError: undefined,
    });
  };

  private updateReviewers = (smeReviewers: ContentAccessUser[]) => {
    this.setState({
      contentReviewers: smeReviewers,
      validationError: undefined,
    });
  };

  private getFirstWorkflow = (): ContentWorkflow | null => {
    const { workflows } = this.props;
    const [fw] = workflows;
    return workflows.length === 1 && fw ? fw : null;
  };

  private validateInput = (isChildInputValid: boolean = true) => {
    const { selectedAttribute, inputVersion } = this.state;
    const inputValue = this.state.inputValue;
    let errorText;

    if (!selectedAttribute || (selectedAttribute !== ContentAttributes.Metadata && !inputValue)) {
      errorText = constants.REQUIRED;
    }

    const allowedUserIdPrefixes = /^(AmazonCorporate_|LoginWithAmazon_|APN_).{1,}$/;
    if (
      selectedAttribute === ContentAttributes.Author &&
      (!inputVersion || !allowedUserIdPrefixes.test(inputValue?.authorId?.trim()))
    ) {
      errorText = constants.REQUIRED;
    }

    // for metadata updates, the content cannot be in locked state
    const isLockedStateValid =
      (selectedAttribute === ContentAttributes.Metadata && !this.props.workflows?.[0]?.lock) ||
      selectedAttribute !== ContentAttributes.Metadata;

    this.setState({
      validationError: errorText,
      isModalInputValid: !errorText && isChildInputValid && isLockedStateValid,
    });
  };

  private createDemoteItems = (workflows: ContentWorkflow[], newAudience: ContentAudience) => {
    const { entities } = this.props.appContext;
    const items = workflows.reduce((acc: DemoteAudienceInfoLineItemWorkflow[], cur) => {
      acc.push({
        audience: cur.contentAudience,
        contentId: cur.contentId,
        contentName: convertEntities(cur.draftContentMetadata.contentName, entities),
        currentState: cur.draftState,
        version: cur.draftWorkflowId,
      });

      acc = acc.concat(
        cur.publicationWorkflows.map((wf) => ({
          audience: wf.contentAudience,
          contentId: wf.contentId,
          contentName: convertEntities(cur.draftContentMetadata.contentName, entities),
          currentState: wf.state,
          version: wf.workflowId,
        })),
      );

      return acc;
    }, []);

    return items.filter((wfi) => {
      let result = true;
      if (wfi.audience === ContentAudience.INTERNAL) {
        result = false;
      }
      if (newAudience === wfi.audience) {
        result = false;
      }
      return result;
    });
  };

  private isRetractReviewEnabled = () => {
    const workflow = this.getFirstWorkflow();
    return workflow?.draftState === WorkflowState.PENDING_REVIEW;
  };

  private retractReviewForm = () => {
    const { entities } = this.props.appContext;
    const workflow = this.getFirstWorkflow();
    const retractMsg = `${constants.CONTENT_RETRACT_REVIEW_MESSAGE} ${convertEntities(workflow?.draftContentMetadata?.contentName ?? '', entities)} ?`;
    return <div>{retractMsg}</div>;
  };

  private retractReview = async () => {
    const { onWorkflowUpdated } = this.props;
    const workflow = this.getFirstWorkflow();
    if (!workflow) return;

    const { draftWorkflowId, contentId } = workflow;
    try {
      await createWorkflowEventWithStatePolling(
        contentId,
        draftWorkflowId,
        WorkflowEventTypes.RETRACT_REVIEW,
        '',
      );
      this.flashbarMessageBroker.pushSuccessMessage(constants.CONTENT_RETRACT_SUCCESS_MESSAGE);
    } catch (e) {
      this.flashbarMessageBroker.pushErrorMessage(constants.CONTENT_RETRACT_ERROR_MESSAGE);
    }
    if (onWorkflowUpdated) {
      onWorkflowUpdated();
    }
  };

  private unlockWorkflowsInputForm = () => {
    return (
      <div>
        Unlock drafts for {this.props.workflows.length} contents? Authors could lose pending
        changes.
      </div>
    );
  };

  private unpublishWorkflowsInputForm = () => {
    const workflow = this.getFirstWorkflow();
    if (!workflow) return <div>{constants.CONFIRM_UNPUBLISH_MODAL_TEXT}</div>;

    const { publicationState, publicationWorkflows } = workflow;
    const isPreviousVersionPublic =
      !!publicationState &&
      ![WorkflowState.DISTRIBUTED, WorkflowState.UNPUBLISHED].includes(publicationState) &&
      publicationWorkflows.some((wf) => [WorkflowState.DISTRIBUTED].includes(wf.state));
    return (
      <div>
        {isPreviousVersionPublic
          ? constants.CONFIRM_REVOKE_AND_UNPUBLISH_MODAL_TEXT
          : constants.CONFIRM_UNPUBLISH_MODAL_TEXT}
      </div>
    );
  };

  private isResetDraftEnabled = (): boolean => {
    return isResetDraftEnabled(this.props.workflows);
  };

  private renderResetDraftInputForm = () => {
    const isRejected = this.props.workflows.some((wf) => wf.draftState === WorkflowState.REJECTED);

    return (
      <SpaceBetween size="s">
        <Box variant="p">Resetting the draft will perform the following actions:</Box>
        <Box variant="small">
          <ul>
            <li>Restore the draft content to the last published version</li>
            <li>
              Save current changes as a working copy, which will be automatically restored the next
              time the author begins editing
            </li>
            <li>Unlock the content for other editors</li>
            <li>Mark the draft as unmodified</li>
            {isRejected && <li>Clear the reviewers and reset the approval count</li>}
          </ul>
        </Box>
      </SpaceBetween>
    );
  };

  private resetDraftAction = async () => {
    const { workflows, onWorkflowUpdated } = this.props;
    await resetDraft(workflows, this.flashbarMessageBroker);
    if (onWorkflowUpdated) {
      onWorkflowUpdated();
    }
  };

  private unlockWorkflows = async () => {
    const { onWorkflowUpdated } = this.props;
    try {
      await Promise.all(
        this.props.workflows.map((wf) => this.mplServiceClient.unlockWorkflow(wf.contentId)),
      );
      this.flashbarMessageBroker.pushSuccessMessage(
        constants.ADMIN_UNLOCK_WORKFLOW_SUCCESS_MESSAGE,
      );
    } catch (e) {
      this.flashbarMessageBroker.pushErrorMessage(constants.ADMIN_UNLOCK_WORKFLOW_ERROR_MESSAGE);
    }
    if (onWorkflowUpdated) {
      onWorkflowUpdated();
    }
  };

  private skipDraftReview = async () => {
    try {
      await Promise.all(
        this.props.workflows.map((wf) =>
          createWorkflowEventWithStatePolling(
            wf.contentId,
            wf.draftWorkflowId,
            WorkflowEventTypes.SKIP_DRAFT_REVIEW,
            '',
          ),
        ),
      );
      this.flashbarMessageBroker.pushSuccessMessage(constants.SKIP_DRAFT_REVIEW_SUCCESS_MESSAGE);
    } catch (e) {
      this.flashbarMessageBroker.pushErrorMessage(constants.SKIP_DRAFT_REVIEW_SUCCESS_MESSAGE);
    }
  };

  private unpublishWorkflows = async () => {
    const workflow = this.getFirstWorkflow();
    if (!workflow) return;

    const { publicationState, publicationWorkflows } = workflow;
    try {
      if (publicationState && [WorkflowState.DISTRIBUTED].includes(publicationState)) {
        this.flashbarMessageBroker.pushErrorMessage(constants.SHOULD_REVOKE_PUBLIC_CONTENT_ERROR);
      } else if (publicationState && [WorkflowState.UNPUBLISHED].includes(publicationState)) {
        this.flashbarMessageBroker.pushErrorMessage(constants.CONTENT_ALREADY_UNPUBLISHED_ERROR);
      } else {
        await createWorkflowEventWithStatePolling(
          workflow.contentId,
          workflow.publicationWorkflowId,
          WorkflowEventTypes.UNPUBLISH,
          '',
        );
        if (publicationWorkflows.some((wf) => [WorkflowState.DISTRIBUTED].includes(wf.state))) {
          // if a previous version is Public
          this.flashbarMessageBroker.pushSuccessMessage(
            constants.CONTENT_REVOKE_AND_UNPUBLISH_SUCCESS_MESSAGE,
          );
        } else {
          this.flashbarMessageBroker.pushSuccessMessage(
            constants.CONTENT_UNPUBLISH_SUCCESS_MESSAGE,
          );
        }
      }
    } catch (e) {
      this.flashbarMessageBroker.pushErrorMessage('Unpublish content failed');
    }

    if (this.props.onWorkflowUnpublished) {
      this.props.onWorkflowUnpublished(this.props.workflows);
    }
  };

  private isEditContentEnabled = () => {
    const workflow = this.getFirstWorkflow();
    if (!workflow) return false;

    return (
      canTechWriterEdit(workflow) &&
      (isPendingToFinalizeWorkflow(workflow) || isRefinalizeWorkflow(workflow))
    );
  };

  private openContent = () => {
    const workflow = this.getFirstWorkflow();
    if (!workflow) return;

    const { contentId, publicationWorkflowId: workflowId } = workflow;
    BrowserHistory.push(`/content-finalizing-viewer/tech-writer/${contentId}/${workflowId}`);
  };
}

export default withAppContext(WorkflowActionControls);
